import React, { useState, useEffect, useContext, memo, useRef, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

// Helper.
import Helper from '../../helpers/Helper';

// Api.
import api from '../../api/Base';

// Hooks.
import { useInstanceStatus } from '../../hooks/useInstanceStatus';
import { useHostStatus } from '../../hooks/useHostStatus';

// Types.
import InstanceTarget from '../../types/instance/InstanceTarget';
import HostTarget from '../../types/host/HostTarget';
import EventType from '../../types/instance/EventType';
import { InstanceContext } from "../../context/InstanceContext";
import { HostContext } from "../../context/HostContext";
import * as dayjs from "dayjs";
import ConditionalRender from "../../helpers/ConditionalRender";
import Status from '../../types/Status';

interface IActionButtonsProps {
    host?: HostTarget,
    instance?: InstanceTarget,
    removeFromList?: Function,
    eventTypes: EventType[],
    isAdmin: boolean,
    onDuplicate?: (item: any) => void,
    rowData?: any,
    status: Status
}

interface IActionsDropdownProps {
    deleteTo: string;
    onDuplicate: () => void;
}

const ActionsDropdown: React.FC<IActionsDropdownProps> = ({ deleteTo, onDuplicate }) => {
    const [show, setShow] = useState<boolean>(false);
    const [menuPosition, setMenuPosition] = useState<{ top: number, right: number }>({ top: 0, right: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);

    useLayoutEffect(() => {
        if (!show || !buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom,
            right: window.innerWidth - rect.right
        });
    }, [show]);

    useEffect(() => {
        if (!show) return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                buttonRef.current && !buttonRef.current.contains(target) &&
                menuRef.current && !menuRef.current.contains(target)
            ) {
                setShow(false);
            }
        };
        const handleScrollOrResize = () => setShow(false);
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [show]);

    return (
        <div className="dropdown" style={{ display: 'inline-block' }}>
            <button
                ref={buttonRef}
                type="button"
                className="btn btn-sm btn-secondary dropdown-toggle"
                onClick={() => setShow(!show)}
                aria-expanded={show}
            >
                <i className="fal fa-ellipsis-h fa-fw"></i><span>Actions</span>
            </button>
            {show && createPortal(
                <ul ref={menuRef}
                    className="dropdown-menu show"
                    style={{
                        display: 'block',
                        position: 'fixed',
                        top: menuPosition.top,
                        right: menuPosition.right,
                        left: 'auto',
                        zIndex: 1050
                    }}>
                    <li>
                        <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => {
                                onDuplicate();
                                setShow(false);
                            }}
                        >
                            <i className="fal fa-copy fa-fw me-2"></i>Duplicate
                        </button>
                    </li>
                    <li>
                        <Link
                            className="dropdown-item text-danger"
                            to={deleteTo}
                            onClick={() => setShow(false)}
                        >
                            <i className="fal fa-trash-alt fa-fw me-2"></i>Delete
                        </Link>
                    </li>
                </ul>,
                document.body
            )}
        </div>
    );
};

const ActionButtonsDisplay: React.FC<IActionButtonsProps> = ({ host, instance, eventTypes, isAdmin, onDuplicate, rowData, status }) => {
    const hostContext = useContext(HostContext);
    const instanceContext = useContext(InstanceContext);

    const lastContactedValue = useMemo(() => {
        if (status.updated) {
            const parsedDate = dayjs.default(status.updated);
            const statusValue = dayjs.default(parsedDate.format('YYYY-MM-DD HH:mm'), 'YYYY-MM-DD HH:mm').fromNow().replace('in a few seconds', 'A few seconds ago')
            return statusValue;
        } else {
            return '-';
        }
    }, [status.updated]);

    const toggleTargetStatus = async (monitor: boolean) => {
        // For both hosts and instances, the status is managed by the hooks
        // The loading/state changes will be reflected when the next poll happens

        let id = 0;
        let url = '';
        let toastMessage = '';
		let data: HostTarget[] | InstanceTarget[] = [];

        if (host) {
            id = host.id;
            url = 'host';

            const updatedHosts = hostContext.hosts.map((obj: HostTarget) =>
                    obj.id === id ? { ...obj, monitor, password: obj.password === '' ? null : obj.password, attempt: obj.attempt + 1, reason: 'User-initiated' } : obj
            );

            hostContext.setHosts(updatedHosts);
            toastMessage = monitor ? 'Stopping host sensor' : 'Starting host sensor';
			data = updatedHosts.filter ((obj: HostTarget) => obj.id === id);
        }

        if (instance) {
            id = instance.id;
            url = 'datasource';

            const updatedInstances = instanceContext.instances.map((obj: InstanceTarget) =>
                    obj.id === id ? { ...obj, monitor, password: obj.password === '' ? null : obj.password, attempt: obj.attempt + 1, reason: 'User-initiated' } : obj
            );

            instanceContext.setInstances(updatedInstances);

            toastMessage = monitor ? 'Stopping instance sensor...' : 'Starting instance sensor...';
			data = updatedInstances.filter ((obj: InstanceTarget) => obj.id === id);
        }

        const result = await api.put(url, data);

        if (result.status !== 200) {
            console.error('Failed to update status', result, data, host || instance, toastMessage);
            return;
        }

        if (instance) {
            Helper.createInstanceEvent(eventTypes, id, monitor ? 'Monitoring Enabled' : 'Monitoring Disabled', monitor ? 'Enabling monitoring' : 'Stopping monitoring');
        }
    };

    let buttonClassName = 'btn btn-sm btn-secondary';
    let iconClassName = 'fal fa-stop fa-fw';
    let buttonTooltip = status.state;
    let buttonTitle = 'Stop';

    if (status.reason) {
        buttonTooltip = `${status.state} - ${status.reason.toLowerCase()}`;
    }

    if (status.state === 'Stopped' || status.state === 'Stopping') {
        buttonClassName = 'btn btn-sm btn-outline-secondary';
        iconClassName = 'fal fa-play fa-fw';
        buttonTitle = 'Start';
    }

    return (
            <>
                <div className='d-flex justify-content-between'>
                    <div className='text-start last-contacted'>
                        {status.loading ? <span /> : `${lastContactedValue}    `}
                    </div>
                    <div className="d-flex gap-1 align-items-center">
                        <ConditionalRender if={isAdmin}>
                            <button
                                    type="button"
                                    className={`${buttonClassName} ${status.loading ? 'loading' : ''}`}
                                    data-tooltip-id="global-tooltip" data-tooltip-content={buttonTooltip}
                                    onClick={() => !status.loading && toggleTargetStatus(buttonTitle !== 'Stop')}
                                    // disabled={status.loading}
                            >
                                <>
                                    <i className={iconClassName}></i>
                                    <span>{buttonTitle}</span>
                                </>
                            </button>
                        </ConditionalRender>
                        {host && (
                                <>
                                    <Link to={`/admin/hosts/edit/${host.id}`} role="button" className="btn btn-sm btn-primary">
                                        <i className="fal fa-edit fa-fw"></i><span>Edit</span>
                                    </Link>
                                    <Link className='btn btn-sm btn-success' to={`/hosts/${host.id}`}>
                                        <i className="fal fa-eye fa-fw"/><span>View</span>
                                    </Link>
                                    <ActionsDropdown
                                        deleteTo={`/admin/hosts/delete/${host.id}`}
                                        onDuplicate={() => onDuplicate && onDuplicate(rowData)}
                                    />
                                </>
                        )}
                        {instance && (
                                <>
                                    <Link to={`/admin/instances/edit/${instance.id}`} role="button" className="btn btn-sm btn-primary">
                                        <i className="fal fa-edit fa-fw"></i><span>Edit</span>
                                    </Link>
                                    <Link className='btn btn-sm btn-success' to={`/instances/${instance.id}/activity`}>
                                        <i className="fal fa-eye fa-fw" /><span>View</span>
                                    </Link>
                                    <ActionsDropdown
                                        deleteTo={`/admin/instances/delete/${instance.id}`}
                                        onDuplicate={() => onDuplicate && onDuplicate(rowData)}
                                    />
                                </>
                        )}
                    </div>
                </div>
            </>
    );
};

interface IConnectedActionButtonsProps extends Omit<IActionButtonsProps, 'status'> {}

const ConnectedActionButtons: React.FC<IConnectedActionButtonsProps> = (props) => {
    const instanceStatus = useInstanceStatus(props.instance?.id || 0);
    const hostStatus = useHostStatus(props.host?.id || 0);
    const status = props.instance ? instanceStatus : hostStatus;

    return <ActionButtonsDisplay {...props} status={status} />;
};

export { ActionButtonsDisplay };
export default memo(ConnectedActionButtons);
